import { flip, offset, shift, useFloating } from '@floating-ui/react';
import { SlotRendering } from '@frontend/common-types';
import { useEffect, useRef, useState } from 'react';

import { getSlotColor as defaultGetSlotColor, getSlotLabel } from '../../utils';

export const useSlotController = <TSlot extends SlotRendering = SlotRendering>(
    slot: TSlot,
    scale: number,
    offsetX: number,
    offsetY: number,
    getSlotColorOverride?: (slot: TSlot) => string
) => {
    const [isHovered, setIsHovered] = useState(false);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const slotRef = useRef<HTMLDivElement>(null);

    const { refs, floatingStyles, update } = useFloating({
        placement: 'right-start',
        strategy: 'fixed',
        middleware: [offset(10), flip(), shift({ padding: 8 })]
    });

    // Update position reference when mouse moves
    useEffect(() => {
        if (isHovered) {
            refs.setPositionReference({
                getBoundingClientRect: () => ({
                    width: 0,
                    height: 0,
                    x: mousePosition.x,
                    y: mousePosition.y,
                    top: mousePosition.y,
                    left: mousePosition.x,
                    right: mousePosition.x,
                    bottom: mousePosition.y
                })
            });
            update();
        }
    }, [mousePosition, refs, isHovered, update]);

    const handleMouseEnter = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        setMousePosition({ x: e.clientX, y: e.clientY });
    };

    const colorClass = getSlotColorOverride ? getSlotColorOverride(slot) : defaultGetSlotColor(slot);
    const padding = 2; // Small gap between slots

    if (!slot.absolute || !slot.dimension) {
        return null;
    }

    // Coordinate system: origin at center (0,0), Y increases upward (mathematical axis)
    // CSS: Y increases downward from top
    // Solution: Invert Y by using negative Y value
    // Important: Y position is the BOTTOM of the slot, so we subtract height to get the top
    const slotLeft = slot.absolute.x * scale + offsetX + padding * scale;
    const slotTop = -(slot.absolute.y + slot.dimension.height) * scale + offsetY + padding * scale;
    const slotWidth = slot.dimension.width * scale - padding * 2 * scale;
    const slotHeight = slot.dimension.height * scale - padding * 2 * scale;

    return {
        slotRef,
        isHovered,
        handleMouseEnter,
        handleMouseLeave,
        handleMouseMove,
        colorClass,
        slotLeft,
        slotTop,
        slotWidth,
        slotHeight,
        floatingRefs: refs,
        floatingStyles,
        label: getSlotLabel(slot)
    };
};
